import DOMPurify from 'dompurify'
import { marked } from 'marked'
import { ref, computed, watch, type Ref } from 'vue'

// Configure marked with secure defaults
marked.use({
  breaks: true,  // Convert line breaks to <br>
  gfm: true,     // Enable GitHub Flavored Markdown
})

/**
 * Composable for markdown processing in Vue components
 */
export function useMarkdown(source: Ref<string>) {
  const processedContent = ref<string>('')
  const isLoading = ref<boolean>(false)

  const processContent = async () => {
    if (!source.value) {
      processedContent.value = ''
      return
    }

    isLoading.value = true
    try {
      // Convert markdown to HTML, then sanitize for security
      const markedResult = marked(source.value)
      const result = typeof markedResult === 'string' ? markedResult : await markedResult
      processedContent.value = DOMPurify.sanitize(result)
    } catch (error) {
      console.error('Markdown processing error:', error)
      processedContent.value = source.value // Fallback to original content
    } finally {
      isLoading.value = false
    }
  }

  // Auto-process when content changes
  watch(source, processContent, { immediate: true })

  return {
    processedContent: computed(() => processedContent.value),
    isLoading: computed(() => isLoading.value),
  }
}

export default useMarkdown